import type { MenuFixedLevels } from './types';
import type { Ref } from 'vue';

import { SCREEN_THRESHOLDS } from './constants';
import { MenuLevel } from './types';

export function useMenuPositioning(menuFixedLevels: Ref<MenuFixedLevels>) {
	function getMenuLevel(screenBottomLevel: number): MenuLevel {
		if (screenBottomLevel >= SCREEN_THRESHOLDS.FULLY_OPEN) {
			return MenuLevel.FULLY_OPEN;
		}
		if (screenBottomLevel >= SCREEN_THRESHOLDS.HALF_OPEN) {
			return MenuLevel.HALF_OPEN;
		}
		return MenuLevel.CLOSED;
	}

	function getPositionForLevel(level: MenuLevel): number {
		if (level === MenuLevel.CLOSED) {
			return 0;
		}
		const height =
			menuFixedLevels.value[level as keyof typeof menuFixedLevels.value];
		if (typeof height !== 'number') {
			console.warn(
				`No height configured for menu level: ${level}. Defaulting to closed position.`
			);
			return 0;
		}
		return window.innerHeight - height;
	}

	function updateMenuLevel(actualMovement: number): MenuLevel {
		// Calculate positions for each level
		const closedPosition = getPositionForLevel(MenuLevel.CLOSED);
		const halfOpenPosition = getPositionForLevel(MenuLevel.HALF_OPEN);
		const fullyOpenPosition = getPositionForLevel(MenuLevel.FULLY_OPEN);

		// Calculate distances to each level
		const distanceToClosed = Math.abs(actualMovement - closedPosition);
		const distanceToHalfOpen = Math.abs(actualMovement - halfOpenPosition);
		const distanceToFullyOpen = Math.abs(actualMovement - fullyOpenPosition);

		// Find the closest level
		const minDistance = Math.min(
			distanceToClosed,
			distanceToHalfOpen,
			distanceToFullyOpen
		);

		if (minDistance === distanceToClosed) {
			return MenuLevel.CLOSED;
		} else if (minDistance === distanceToHalfOpen) {
			return MenuLevel.HALF_OPEN;
		} else {
			return MenuLevel.FULLY_OPEN;
		}
	}

	function getSmallestMenuHeight(): number {
		return Math.min(...Object.values(menuFixedLevels.value));
	}

	function getMaxDragLimit(): number {
		return window.innerHeight - getSmallestMenuHeight();
	}

	return {
		getMenuLevel,
		getPositionForLevel,
		updateMenuLevel,
		getSmallestMenuHeight,
		getMaxDragLimit
	};
}
